import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  SimpleGrid,
  Skeleton,
  Text,
  useColorModeValue,
  useToast,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { MdKeyboardArrowDown } from "react-icons/md";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  apiListSimulations,
  type SavedSimulationRow,
  type SimulationStatusCode,
} from "api/simulation/simulationsList";
import { uniquePurchaseOrderRefs } from "api/simulation/uniquePurchaseOrderRefs";
import {
  apiPatchSimulationStatus,
  type ManualSimulationStatusCode,
} from "api/simulation/updateSimulationStatus";
import { useAuth } from "contexts/AuthProvider";
import { MOCK_SAVED_SIMULATIONS } from "../fixtures/savedSimulationsMock";

const MANUAL_STATUS_OPTIONS: {
  code: ManualSimulationStatusCode;
  label: string;
}[] = [{ code: "failed", label: "Fallado" }];

const STATUS_DISPLAY_LABELS: Record<SimulationStatusCode, string> = {
  draft: "Planificado",
  running: "Corriendo",
  completed: "Completado",
  failed: "Fallado",
  archived: "Archivado",
  real: "Real",
};

const STATUS_FILTERS: { code: SimulationStatusCode | "all"; label: string }[] = [
  { code: "all", label: "Todas" },
  { code: "draft", label: "Planificado" },
  { code: "completed", label: "Completado" },
  { code: "failed", label: "Fallado" },
];

function statusAccent(status: SimulationStatusCode): string {
  switch (status) {
    case "draft":
      return "orange.400";
    case "running":
      return "brand.400";
    case "completed":
      return "green.400";
    case "failed":
      return "red.400";
    case "archived":
      return "gray.500";
    case "real":
      return "blue.400";
    default:
      return "gray.500";
  }
}

function statusLabel(status: SimulationStatusCode): string {
  return STATUS_DISPLAY_LABELS[status] ?? status;
}

function statusBadgeColorScheme(
  status: SimulationStatusCode
): string {
  switch (status) {
    case "failed":
      return "red";
    case "completed":
      return "green";
    case "running":
      return "purple";
    case "archived":
      return "gray";
    case "real":
      return "blue";
    default:
      return "orange";
  }
}

function formatSimulationInstant(row: SavedSimulationRow): string {
  const raw = row.executed_at ?? row.created_at;
  if (!raw) {
    return "—";
  }
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) {
    return raw;
  }
  return new Intl.DateTimeFormat("es-AR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(d);
}

/**
 * Por defecto usa el fixture (validación de UI). API con
 * `REACT_APP_SIMULATOR_SAVED_USE_API=true`. `?mockSaved=1` fuerza fixture;
 * `?useSavedApi=1` fuerza API si la env lo permite.
 */
function useSavedListMockupMode(): boolean {
  const [searchParams] = useSearchParams();
  if (searchParams.get("mockSaved") === "1") {
    return true;
  }
  if (searchParams.get("useSavedApi") === "1") {
    return false;
  }
  return process.env.REACT_APP_SIMULATOR_SAVED_USE_MOCK === "true";
}

type SimulationCardProps = {
  row: SavedSimulationRow;
  index: number;
  cardBg: string;
  cardBorder: string;
  subtle: string;
  titleColor: string;
  statusChangeEnabled: boolean;
  isStatusUpdating: boolean;
  isLoadInProgress: boolean;
  onSelect: (row: SavedSimulationRow) => void;
  onManualStatusChange: (
    id: string,
    status: ManualSimulationStatusCode
  ) => void;
};

function SimulationCard({
  row,
  index,
  cardBg,
  cardBorder,
  subtle,
  titleColor,
  statusChangeEnabled,
  isStatusUpdating,
  isLoadInProgress,
  onSelect,
  onManualStatusChange,
}: SimulationCardProps) {
  const accent = statusAccent(row.status);
  const orders = uniquePurchaseOrderRefs(row.simulated_purchase_orders);
  const badgeScheme = statusBadgeColorScheme(row.status);

  const openSimulation = () => {
    if (!isLoadInProgress && !isStatusUpdating) {
      onSelect(row);
    }
  };

  const blockCardOpen = (event: { stopPropagation: () => void }) => {
    event.stopPropagation();
  };

  return (
    <Box
      position="relative"
      pl="14px"
      py={5}
      px={5}
      borderRadius="2px"
      bg={cardBg}
      borderWidth="1px"
      borderColor={cardBorder}
      boxShadow="dark-lg"
      overflow="hidden"
      transition="transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.35s ease"
      aria-busy={isLoadInProgress}
      _before={{
        content: '""',
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        width: "5px",
        bg: accent,
        opacity: 0.95,
      }}
      _hover={{
        transform: isLoadInProgress ? undefined : "translateY(-6px)",
        boxShadow: isLoadInProgress
          ? undefined
          : "0 22px 50px rgba(0,0,0,0.35)",
      }}
      sx={{
        animation: `cardIn 0.55s cubic-bezier(0.22, 1, 0.36, 1) ${index * 0.06}s both`,
        "@keyframes cardIn": {
          from: { opacity: 0, transform: "translateY(16px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      <Box
        position="absolute"
        right="-18%"
        top="-40%"
        w="180px"
        h="180px"
        bgGradient="linear(to-br, rgba(117,81,255,0.14), transparent)"
        borderRadius="full"
        pointerEvents="none"
      />
      <VStack align="stretch" spacing={3} position="relative">
        <Heading
          size="sm"
          color={titleColor}
          fontWeight="700"
          letterSpacing="-0.03em"
          lineHeight="1.25"
          role="button"
          tabIndex={0}
          aria-label={`Abrir simulación ${row.simulation_name || row.id}`}
          cursor={isLoadInProgress ? "wait" : "pointer"}
          onClick={openSimulation}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              openSimulation();
            }
          }}
          _hover={{ textDecoration: isLoadInProgress ? undefined : "underline" }}
        >
          {row.simulation_name || `Simulación #${row.id}`}
        </Heading>
        <Text fontSize="xs" color={subtle} letterSpacing="0.02em" textTransform="uppercase">
          Fecha y hora
        </Text>
        <Flex
          align="center"
          justify="space-between"
          gap={3}
          wrap="wrap"
          onClick={blockCardOpen}
          onMouseDown={blockCardOpen}
        >
          <Text fontSize="sm" color={titleColor} fontWeight="500" pointerEvents="none">
            {formatSimulationInstant(row)}
          </Text>
          <Menu placement="bottom-end" isLazy>
            <MenuButton
              as={Button}
              size="xs"
              variant="subtle"
              colorScheme={badgeScheme}
              fontWeight="600"
              fontSize="xs"
              borderRadius="sm"
              px={2}
              py={0.5}
              h="auto"
              minH="unset"
              rightIcon={<MdKeyboardArrowDown />}
              isLoading={isStatusUpdating}
              isDisabled={!statusChangeEnabled || isStatusUpdating}
              aria-label={`Cambiar estado de ${row.simulation_name || row.id}`}
            >
              {statusLabel(row.status)}
            </MenuButton>
            <MenuList minW="10rem" zIndex={10}>
              {MANUAL_STATUS_OPTIONS.map((opt) => (
                <MenuItem
                  key={opt.code}
                  fontSize="sm"
                  isDisabled={row.status === opt.code}
                  onClick={() => onManualStatusChange(row.id, opt.code)}
                >
                  {opt.label}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        </Flex>
        <Box
          role="button"
          tabIndex={0}
          cursor={isLoadInProgress ? "wait" : "pointer"}
          onClick={openSimulation}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              openSimulation();
            }
          }}
        >
          <Text fontSize="xs" color={subtle} letterSpacing="0.02em" textTransform="uppercase" mb={2}>
            Órdenes de compra ({orders.length})
          </Text>
          {orders.length === 0 ? (
            <Text fontSize="sm" color={subtle} fontStyle="italic">
              Sin órdenes vinculadas
            </Text>
          ) : (
            <Wrap spacing={2} shouldWrapChildren>
              {orders.slice(0, 12).map((po) => (
                <WrapItem key={po.id}>
                  <Badge
                    variant="outline"
                    fontSize="0.68rem"
                    borderRadius="sm"
                    borderColor="whiteAlpha.300"
                    color={titleColor}
                    fontWeight="500"
                  >
                    {po.document_number}
                  </Badge>
                </WrapItem>
              ))}
              {orders.length > 12 ? (
                <WrapItem>
                  <Text fontSize="xs" color={subtle}>
                    +{orders.length - 12} más
                  </Text>
                </WrapItem>
              ) : null}
            </Wrap>
          )}
        </Box>
      </VStack>
    </Box>
  );
}

export type SavedSimulationsBoardProps = {
  /**
   * Si true, se puede usar el API (además de la env `REACT_APP_SIMULATOR_SAVED_USE_API`).
   * Durante sesión de cobertura suele ser false para no competir con requests del simulador;
   * en modo fixture mock no cambia la visual.
   */
  fetchFromApi: boolean;
  /** Id de simulación en carga (click en card). */
  loadingSimulationId?: string | null;
  /** Al hacer click en una card (histórico). */
  onSelectSimulation?: (row: SavedSimulationRow) => void;
};

export function SavedSimulationsBoard({
  fetchFromApi,
  loadingSimulationId = null,
  onSelectSimulation,
}: SavedSimulationsBoardProps) {
  const { logoutAction } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const mockupMode = useSavedListMockupMode();
  const [filter, setFilter] = useState<SimulationStatusCode | "all">("all");
  const [mockStatusOverrides, setMockStatusOverrides] = useState<
    Record<string, SimulationStatusCode>
  >({});
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const panelBg = useColorModeValue("white", "navy.800");
  const panelBorder = useColorModeValue("secondaryGray.200", "whiteAlpha.200");
  const titleColor = useColorModeValue("secondaryGray.900", "white");
  const subtle = useColorModeValue("secondaryGray.600", "whiteAlpha.600");
  const cardBg = useColorModeValue("rgba(255,255,255,0.92)", "rgba(17,28,68,0.92)");
  const cardBorder = useColorModeValue("secondaryGray.300", "whiteAlpha.200");
  const stripBg = useColorModeValue("secondaryGray.100", "navy.900");
  const mockBannerBg = useColorModeValue("orange.50", "whiteAlpha.100");
  const mockBannerBorder = useColorModeValue("orange.200", "whiteAlpha.200");

  const mockRows = useMemo(() => {
    if (!mockupMode) {
      return [];
    }
    const withOverrides = MOCK_SAVED_SIMULATIONS.map((r) => ({
      ...r,
      status: mockStatusOverrides[r.id] ?? r.status,
    }));
    if (filter === "all") {
      return withOverrides;
    }
    return withOverrides.filter((r) => r.status === filter);
  }, [mockupMode, filter, mockStatusOverrides]);

  const queryKey = useMemo(
    () => ["simulations-saved-list", filter] as const,
    [filter]
  );

  const apiEnabled = fetchFromApi && !mockupMode;

  const { data, isLoading, isError } = useQuery(
    queryKey,
    () =>
      apiListSimulations(
        filter === "all" ? undefined : { status: filter }
      ),
    {
      enabled: apiEnabled,
      retry: 1,
      onError: (err: unknown) => {
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 403) {
          logoutAction(true);
        }
      },
    }
  );

  const statusPatchMut = useMutation(
    ({ id, status }: { id: string; status: ManualSimulationStatusCode }) =>
      apiPatchSimulationStatus(id, status),
    {
      onSuccess: (_row, { status: newStatus }) => {
        queryClient.invalidateQueries(["simulations-saved-list"]);
        toast({
          title: "Estado actualizado",
          description: `La simulación pasó a ${statusLabel(newStatus)}.`,
          status: "success",
          duration: 4000,
        });
      },
      onError: () => {
        toast({
          title: "No se pudo cambiar el estado",
          status: "error",
          duration: 5000,
        });
      },
      onSettled: () => {
        setUpdatingId(null);
      },
    }
  );

  const handleSelectSimulation = (row: SavedSimulationRow) => {
    if (mockupMode) {
      toast({
        title: "Modo demo",
        description:
          "Esta tarjeta usa datos de ejemplo. Activá el API (`REACT_APP_SIMULATOR_SAVED_USE_MOCK=false`) para abrir simulaciones reales.",
        status: "info",
        duration: 5000,
      });
      return;
    }
    onSelectSimulation?.(row);
  };

  const handleManualStatusChange = (
    id: string,
    newStatus: ManualSimulationStatusCode
  ) => {
    if (mockupMode) {
      setMockStatusOverrides((prev) => ({ ...prev, [id]: newStatus }));
      toast({
        title: "Estado actualizado (mock)",
        description: `La simulación pasó a ${statusLabel(newStatus)}.`,
        status: "success",
        duration: 3000,
      });
      return;
    }
    setUpdatingId(id);
    statusPatchMut.mutate({ id, status: newStatus });
  };

  const rows = mockupMode ? mockRows : data ?? [];
  const isLoadingRows = mockupMode ? false : isLoading;
  const isErrorRows = mockupMode ? false : isError;
  const statusChangeEnabled = mockupMode || apiEnabled;

  return (
    <Box
      mb={6}
      p={{ base: 5, md: 8 }}
      borderRadius="3px"
      bg={panelBg}
      borderWidth="1px"
      borderColor={panelBorder}
      position="relative"
      overflow="hidden"
      sx={{
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          background:
            "repeating-linear-gradient(-12deg, transparent, transparent 38px, rgba(117,81,255,0.03) 38px, rgba(117,81,255,0.03) 40px)",
          pointerEvents: "none",
        },
      }}
    >
      <Box position="relative" zIndex={1}>
        {/* {mockupMode ? (
          <Box
            mb={4}
            py={2}
            px={3}
            borderRadius="md"
            bg={mockBannerBg}
            borderWidth="1px"
            borderColor={mockBannerBorder}
          >
            <Text fontSize="sm" color={titleColor} fontWeight="600">
              Vista con datos de ejemplo: sirve para validar diseño y filtros hasta que el
              listado viva en el API.{" "}
              <Text as="span" fontWeight="400" color={subtle}>
                Para consumir{" "}
                <Text as="span" fontFamily="mono" fontSize="xs">
                  GET /simulations/
                </Text>{" "}
                definí{" "}
                <Text as="span" fontFamily="mono" fontSize="xs">
                  REACT_APP_SIMULATOR_SAVED_USE_API=true
                </Text>
                . Podés forzar fixture con{" "}
                <Text as="span" fontFamily="mono" fontSize="xs">
                  ?mockSaved=1
                </Text>
                .
              </Text>
            </Text>
          </Box>
        ) : null} */}

        <Flex
          direction={{ base: "column", lg: "row" }}
          align={{ base: "stretch", lg: "flex-end" }}
          justify="space-between"
          gap={6}
          mb={8}
        >
          <Box>
            <Text
              fontSize="xs"
              fontWeight="700"
              letterSpacing="0.28em"
              textTransform="uppercase"
              color={subtle}
              mb={1}
            >
              Cobertura · historial
            </Text>
            <Heading
              size="lg"
              color={titleColor}
              fontWeight="800"
              letterSpacing="-0.04em"
              lineHeight="1.1"
            >
              Simulaciones guardadas
            </Heading>
            <Text mt={2} fontSize="sm" color={subtle} maxW="lg">
              Escenario, momento de ejecución, estado operativo y pedidos simulados
              asociados a cada corrida. Hacé click en el título o en las órdenes de compra para reabrirla.
            </Text>
          </Box>
        </Flex>

        <Box
          mb={8}
          py={3}
          px={{ base: 3, md: 4 }}
          borderRadius="md"
          bg={stripBg}
          borderWidth="1px"
          borderColor={panelBorder}
        >
          <Text
            fontSize="xs"
            fontWeight="700"
            letterSpacing="0.18em"
            textTransform="uppercase"
            color={subtle}
            mb={3}
          >
            Filtrar por estado
          </Text>
          <Flex flexWrap="wrap" gap={2}>
            {STATUS_FILTERS.map((opt) => {
              const active = filter === opt.code;
              return (
                <Button
                  key={opt.code}
                  size="sm"
                  variant={active ? "solid" : "ghost"}
                  colorScheme={active ? "brand" : "gray"}
                  borderRadius="full"
                  fontWeight="600"
                  onClick={() => setFilter(opt.code)}
                >
                  {opt.label}
                </Button>
              );
            })}
          </Flex>
        </Box>

        {isErrorRows ? (
          <Text color="red.400" fontSize="sm">
            No se pudo cargar el listado. Intentá de nuevo más tarde.
          </Text>
        ) : null}

        {isLoadingRows ? (
          <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={6}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} height="200px" borderRadius="md" startColor="whiteAlpha.100" endColor="whiteAlpha.300" />
            ))}
          </SimpleGrid>
        ) : rows.length === 0 ? (
          <Text color={subtle} fontSize="sm">
            No hay simulaciones para este filtro.
          </Text>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={6}>
            {rows.map((row, index) => (
              <SimulationCard
                key={row.id}
                row={row}
                index={index}
                cardBg={cardBg}
                cardBorder={cardBorder}
                subtle={subtle}
                titleColor={titleColor}
                statusChangeEnabled={statusChangeEnabled}
                isStatusUpdating={updatingId === row.id}
                isLoadInProgress={loadingSimulationId === row.id}
                onSelect={handleSelectSimulation}
                onManualStatusChange={handleManualStatusChange}
              />
            ))}
          </SimpleGrid>
        )}
      </Box>
    </Box>
  );
}
